import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debug from 'debug';
import {
  Settings,
  History,
  Bell,
  Sparkles,
  MessageCircleQuestion,
  Download,
  AlertCircle,
  Users,
  Tag,
} from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  LinearProgress,
  Typography,
  Box,
  Collapse,
  Fade,
} from '@mui/material';
import type { ListProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ActionType } from '../../../types';
import { getSettingsUrl } from '../../../lib/routingUtils';
import type { RootState } from '../../../reducers';
import useUserTags from '../../../hooks/useTags';
import Changelog from '../../common/Changelog';
import { LATEST_VERSION } from '../../common/Changelog/changelogData';
import Notifications from '../toolbar/Notifications';
import { useLimits } from '../../plan/hooks/limits';
import HistoryButton from './HistoryButton';

import { ContactSupportDialog } from '../../support';
import {
  preloadChatHistory,
  selectIsPreloading,
} from '../../support/ai-assistant/chatPreloadSlice';

const logger = debug('screenapp:components:DrawerActions');

type DrawerActionKeyType =
  | 'refer-earn'
  | 'help'
  | 'install-addon'
  | 'contact-support'
  | 'old-screenapp'
  | 'settings'
  | 'changelog'
  | 'notifications'
  | 'ai-usage'
  | 'history';
type DrawerActionType = ActionType<DrawerActionKeyType> & {
  customContent?: React.ReactNode;
};

export type SubSettingKeyType = 'profile' | 'team' | 'recording' | 'plugin';
export const DrawerActions = (listProps: ListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dashboard } = useSelector((state: RootState) => ({
    dashboard: state.dashboard,
  }));
  const open = dashboard.drawerState === 'open';
  const isPreloadingChat = useSelector(selectIsPreloading);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [notificationIcon, setNotificationIcon] = useState<React.ReactNode>(
    <Bell />
  );

  // const noTeamMembers = team.selectedTeam?.teamSpaceMembers.length === 0;
  const { getTag, setTag } = useUserTags();

  const showHistoryAtTop = getTag('feature_history_btn_top') === 'true';
  // Disabled notification badges due to low engagement
  // const hasClickedExtension = Boolean(getTag('chrome-extension-clicked'));
  // const hasSeenLatestChangelog =
  //   getTag('changelog-seen-version') === LATEST_VERSION;
  // const showChangelogNotification = !hasSeenLatestChangelog;
  const { notifications } = dashboard;
  const unreadCount = notifications.filter((n) => !n.read).length;
  const hasUnreadNotifications = unreadCount > 0;
  const { packageProperties, usage } = useSelector(
    (state: RootState) => state?.plan || {}
  );
  const guardLimits = useLimits();
  const theme = useTheme();

  // Function to get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'transcription':
        return <Sparkles />; // AI processing complete
      case 'import':
        return <Download />; // Import/upload complete
      case 'tag':
        return <Tag />;
      case 'team':
        return <Users />;
      case 'error':
        return <AlertCircle />;
      case 'processing':
        return <Settings />;
      default:
        return <Bell />;
    }
  };

  // Watch for new notifications and update icon
  useEffect(() => {
    // Get unread notifications
    const unreadNotifications = notifications.filter((n) => !n.read);

    if (unreadNotifications.length > 0) {
      // If there are unread notifications, show icon of the latest unread one
      const latestUnreadNotification = unreadNotifications.sort(
        (a, b) => b.timestamp - a.timestamp
      )[0];
      const newIcon = getNotificationIcon(latestUnreadNotification.type);
      setNotificationIcon(newIcon);
    } else {
      // If no unread notifications (all read or none exist), show default bell
      setNotificationIcon(<Bell />);
    }
  }, [notifications]);

  // Handler for preloading chat on hover
  const handleSupportHover = () => {
    if (!isPreloadingChat) {
      dispatch(preloadChatHistory() as any);
    }
  };

  const handleClick = (actionKey: DrawerActionKeyType) => {
    switch (actionKey) {
      case 'help':
        try {
          window?.Userback.open();
        } catch (error) {
          logger.log('Error opening Userback', error);
        }
        break;
      case 'old-screenapp':
        window.open('/legacy', '_blank');
        break;
      case 'settings':
        setSettingsOpen(!settingsOpen);
        break;
      case 'changelog':
        setChangelogOpen(true);
        setTag({
          key: 'changelog-seen-version',
          value: LATEST_VERSION,
        });
        break;
      case 'notifications':
        setNotificationsOpen(true);
        break;

      case 'contact-support':
        setSupportDialogOpen(true);
        break;
      default:
        break;
    }
  };

  const handleAIUsageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    guardLimits('unlimitedCredits', () => {
      window.location.href = '/settings/usage';
    });
  };

  const baseDrawerActions: DrawerActionType[] = [
    {
      key: 'changelog',
      label: t('drawer.actions.whatsNew'),
      startIcon: (
        <Badge
          color="secondary"
          variant="dot"
          invisible
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ff4270',
              width: '6px',
              height: '6px',
              minWidth: '6px',
            },
          }}
        >
          <History />
        </Badge>
      ),
      onClick: () => handleClick('changelog'),
    },
    {
      key: 'notifications',
      label: t('drawer.actions.notifications'),
      startIcon: (
        <Badge
          color="secondary"
          variant="dot"
          invisible={!hasUnreadNotifications}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ff4270',
              width: '6px',
              height: '6px',
              minWidth: '6px',
            },
          }}
        >
          {notificationIcon}
        </Badge>
      ),
      onClick: () => setNotificationsOpen(true),
    },
    {
      key: 'settings',
      label: t('drawer.actions.settings'),
      startIcon: <Settings />,
      href: getSettingsUrl({ withBasePath: true }),
    },
  ];

  const historyAction: DrawerActionType = {
    key: 'history',
    label: t('drawer.actions.history', 'History'),
    startIcon: null,
    customContent: <HistoryButton open={open} />,
    onClick: () => {}, // Handled by HistoryButton component
  };

  const aiUsageAction: DrawerActionType | null =
    packageProperties?.askAICount && packageProperties.askAICount <= 998
      ? {
          key: 'ai-usage' as DrawerActionKeyType,
          label: t('drawer.actions.aiUsage.count', {
            used: usage?.askAICount || 0,
            total: packageProperties.askAICount,
          }),
          startIcon: open ? null : <Sparkles />,
          customContent: open ? (
            <Box
              sx={{
                flex: 1,
                cursor: 'pointer',
                px: open ? 1.5 : 1.25,
                mx: 0.5,
                borderRadius: 20,
                minHeight: '36px',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'transparent',
                '&:hover': {
                  '&::before': {
                    opacity: 1,
                  },
                  '& .MuiLinearProgress-root': {
                    backgroundColor: theme.palette.action.hover,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  backgroundColor: 'secondary.light',
                  opacity: 0,
                  borderRadius: 'inherit',
                },
              }}
              onClick={handleAIUsageClick}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  py: 1,
                  pl: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                    pr: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondaryContrastText"
                  >
                    {t('drawer.actions.aiUsage.title')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondaryContrastText"
                  >
                    {t('drawer.actions.aiUsage.count', {
                      used: usage?.askAICount || 0,
                      total:
                        packageProperties.askAICount > 998
                          ? t('drawer.actions.aiUsage.unlimited')
                          : packageProperties.askAICount,
                    })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    packageProperties.askAICount > 998
                      ? 0
                      : ((usage?.askAICount || 0) /
                          packageProperties.askAICount) *
                        100
                  }
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'secondary.light',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      '& .MuiLinearProgress-bar1Buffer': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          ) : null,
          onClick: handleAIUsageClick,
        }
      : null;

  const mobileAppOrSupportAction: DrawerActionType = {
    key: 'contact-support',
    label: t('drawer.actions.contactSupport'),
    startIcon: <MessageCircleQuestion />,
    onClick: () => handleClick('contact-support'),
    onMouseEnter: handleSupportHover,
  };

  const drawerActions = [
    baseDrawerActions[0], // What's New
    ...(hasUnreadNotifications ? [baseDrawerActions[1]] : []), // Notifications (conditional)
    ...(!showHistoryAtTop ? [historyAction] : []), // History (only show in Other section if not at top)
    mobileAppOrSupportAction, // Contact Support
    baseDrawerActions[2], // Settings
    ...(aiUsageAction ? [aiUsageAction] : []), // AI Usage (conditional)
  ];

  return (
    <>
      <ListItemText
        primary={t('drawer.actions.other')}
        sx={{
          opacity: open ? 1 : 0,
          transition: theme.transitions.create(['opacity'], {
            duration: open ? 150 : 100,
            easing: theme.transitions.easing.easeOut,
            delay: open ? 50 : 0,
          }),
          '& .MuiTypography-root': {
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'secondary.contrastText',
            textTransform: 'uppercase',
            px: 2,
            pt: 2,
          },
        }}
      />
      <List
        {...listProps}
        sx={{
          py: 0.5,
          '& .MuiListItem-root:not(:last-child)': {
            mb: 0.5,
          },
        }}
      >
        {drawerActions.map(
          ({
            key,
            label,
            startIcon,
            onClick,
            onMouseEnter,
            href,
            target,
            customContent,
          }) => (
            <Collapse
              key={key}
              in={key !== 'notifications' || hasUnreadNotifications}
              timeout={{
                enter: 150,
                exit: 100,
              }}
              easing={{
                enter: theme.transitions.easing.easeOut,
                exit: theme.transitions.easing.easeIn,
              }}
              unmountOnExit
            >
              <Fade
                in={key !== 'notifications' || hasUnreadNotifications}
                timeout={{
                  enter: 100,
                  exit: 75,
                }}
                easing={{
                  enter: theme.transitions.easing.easeOut,
                  exit: theme.transitions.easing.easeIn,
                }}
              >
                <ListItem disablePadding sx={{ display: 'block' }}>
                  {customContent || (
                    <Tooltip
                      title={label}
                      placement="right"
                      arrow
                      disableHoverListener={open}
                    >
                      <ListItemButton
                        {...(href ? { href } : {})}
                        {...(target ? { target } : {})}
                        onClick={onClick}
                        onMouseEnter={onMouseEnter as any}
                        sx={{
                          justifyContent: open ? 'initial' : 'center',
                          px: open ? 2.5 : 1.25,
                          borderRadius: 20,
                          mx: 0.5,
                          minHeight: '36px',
                          position: 'relative',
                          overflow: 'hidden',
                          bgcolor: 'transparent',
                          '&.Mui-selected': {
                            bgcolor: 'transparent',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                            backgroundColor: 'secondary.light',
                            opacity: 0,
                            borderRadius: 'inherit',
                          },
                          '&:hover': {
                            '&::before': {
                              opacity: 1,
                              backgroundColor: theme.palette.action.hover,
                            },
                          },
                          '& > *': {
                            position: 'relative',
                            zIndex: 1,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                            pl: open ? 0 : 0.5,
                            color: 'text.secondary',
                            transition: theme.transitions.create(
                              ['margin', 'padding', 'color'],
                              {
                                duration: 150,
                                easing: theme.transitions.easing.easeOut,
                              }
                            ),
                            '& svg': {
                              color: 'inherit',
                            },
                          }}
                        >
                          {startIcon}
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          sx={{
                            opacity: open ? 1 : 0,
                            transition: theme.transitions.create(['opacity'], {
                              duration: open ? 150 : 100,
                              easing: theme.transitions.easing.easeOut,
                              delay: open ? 50 : 0,
                            }),
                            '& .MuiTypography-root': {
                              fontWeight:
                                // Disabled changelog badge: (key === 'changelog' && showChangelogNotification) ||
                                key === 'notifications' &&
                                hasUnreadNotifications
                                  ? 700
                                  : 400,
                              color: 'text.secondary',
                            },
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  )}
                </ListItem>
              </Fade>
            </Collapse>
          )
        )}
      </List>
      {changelogOpen && (
        <Changelog
          open={changelogOpen}
          onClose={() => setChangelogOpen(false)}
        />
      )}
      <Notifications
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />

      {supportDialogOpen && (
        <ContactSupportDialog
          onClose={() => setSupportDialogOpen(false)}
          formSource="Drawer - Contact Support"
        />
      )}
    </>
  );
};

export default DrawerActions;
